import type { Material, MaterialId } from "../types";
import { hexToRgb, mix, rgbToHex, shade } from "./color";

/**
 * Procedural material swatches.
 *
 * Every material is painted onto a small square tile whose pixels represent
 * a fixed number of real-world inches (`TILE_IN`). Renderers draw parts in an
 * inch-space coordinate system and use the tile as a repeating fill pattern
 * scaled by `1 / PX_PER_IN`, so a fabric weave is the same physical size on a
 * 5' loveseat and a 12' sectional regardless of zoom.
 *
 * Tiles are cached per `(material, fill color, strength)`. No image assets:
 * the whole system is a few hundred lines of canvas drawing that runs once
 * per unique swatch and is a no-op in jsdom (returns `null` → flat fill).
 */

/** Real-world size of one tile, inches. */
export const TILE_IN = 8;
/** Tile resolution, px per inch. 16 px/in is crisp up to ~12x zoom. */
export const PX_PER_IN = 16;
const TILE_PX = TILE_IN * PX_PER_IN;

export interface MaterialInfo {
  id: MaterialId;
  label: string;
  /** Suggested tints shown in the material picker. */
  swatches: string[];
  /** Grain follows the part's long axis (wood, brushed metal). */
  directional: boolean;
}

export const MATERIALS: MaterialInfo[] = [
  { id: "fabric", label: "Fabric", swatches: ["#4f6c8f", "#7c8fa8", "#8a7f6e", "#5e6b5a", "#a3665c", "#3b3f4a"], directional: false },
  { id: "boucle", label: "Bouclé", swatches: ["#e7e0d3", "#d7cdbd", "#b9b3a6", "#8d867a"], directional: false },
  { id: "velvet", label: "Velvet", swatches: ["#2f4f6b", "#6e3b4d", "#3f6b4f", "#7c6a3a", "#3a3a5c"], directional: true },
  { id: "leather", label: "Leather", swatches: ["#6e4a2f", "#3d2b1f", "#9a6b45", "#2a2a2e", "#b98a5e"], directional: false },
  { id: "wood-light", label: "Light wood", swatches: ["#d8b98a", "#c9ad7f", "#e2c9a0", "#b99a6b"], directional: true },
  { id: "wood-mid", label: "Oak", swatches: ["#a07b54", "#8a6a47", "#9a7a52", "#7e6242"], directional: true },
  { id: "wood-dark", label: "Walnut", swatches: ["#5c4a3f", "#6e574a", "#4a3a30", "#3a2d25"], directional: true },
  { id: "marble", label: "Marble", swatches: ["#ececea", "#dcdcd8", "#1f1f22", "#c9c2b6"], directional: false },
  { id: "glass", label: "Glass", swatches: ["#b9dbe4", "#cfe6ec"], directional: false },
  { id: "metal", label: "Metal", swatches: ["#cfd2d8", "#a9adb6", "#8c8f96", "#3a3a44", "#d9c9a3"], directional: true },
  { id: "rattan", label: "Rattan", swatches: ["#c8a970", "#b89860", "#a4834f"], directional: false },
  { id: "wool-rug", label: "Wool", swatches: ["#c39a72", "#b7a58e", "#8c7a6a", "#5b6470", "#a15d4d"], directional: false },
  { id: "ceramic", label: "Ceramic", swatches: ["#b8704f", "#e9e4dc", "#3b4a5a", "#8a9a8a"], directional: false },
  { id: "plastic", label: "Plastic", swatches: ["#f1f1f1", "#2b2b2f", "#e04b3a", "#2a70f5"], directional: false },
  { id: "foliage", label: "Foliage", swatches: ["#5e8a5e", "#4a7a4f", "#7aa66a", "#3f6b45"], directional: false },
  { id: "matte", label: "Matte", swatches: ["#94a3b8", "#3a3a44", "#e0d8c8", "#cfd2d8", "#5e8a5e"], directional: false },
  { id: "quartz", label: "Quartz", swatches: ["#f3f1ec", "#e7e1d6", "#c8c5be", "#4a4a4e", "#d9d0c4"], directional: false },
  { id: "granite", label: "Granite", swatches: ["#c4b5a0", "#2c2c30", "#e4e2dc", "#8a6d52", "#9a9b9e"], directional: false },
  { id: "quartzite", label: "Quartzite", swatches: ["#e6dcc8", "#f3f1ec", "#c5c8c2", "#2a2a2c"], directional: false },
  { id: "soapstone", label: "Soapstone", swatches: ["#3e4544", "#5c6560", "#2f3332", "#4a453f"], directional: false },
  { id: "butcher-block", label: "Butcher block", swatches: ["#e2c49a", "#c9a36a", "#8b6744", "#d4b48a"], directional: true },
  { id: "concrete", label: "Concrete", swatches: ["#c8c6c1", "#b7b1a8", "#5a5856", "#e6e4df"], directional: false },
  { id: "terrazzo", label: "Terrazzo", swatches: ["#eceae6", "#e4d5c4", "#3a3a3c", "#6d7a72"], directional: false },
  { id: "laminate", label: "Laminate", swatches: ["#e4e0d8", "#c4a574", "#d0ccc6", "#2e2e30"], directional: false },
  { id: "tile", label: "Tile", swatches: ["#f7f5f2", "#e6dcc8", "#cfcbc4", "#c47a58", "#6e8a78"], directional: false },
];

/** Materials that belong in the countertop section of the picker. */
export const COUNTERTOP_MATERIAL_IDS: ReadonlySet<MaterialId> = new Set([
  "quartz",
  "granite",
  "quartzite",
  "soapstone",
  "butcher-block",
  "concrete",
  "terrazzo",
  "laminate",
  "tile",
]);

export function materialInfo(id: MaterialId): MaterialInfo {
  return MATERIALS.find((m) => m.id === id) ?? MATERIALS[MATERIALS.length - 1];
}

export interface MaterialTile {
  canvas: HTMLCanvasElement;
  /** Lazily computed data URL for SVG `<pattern>` consumers. */
  dataUrl: () => string;
}

const tileCache = new Map<string, MaterialTile | null>();
const TILE_CACHE_CAP = 240;

/**
 * Get (or paint) the tile for a material rendered over `fill`. `strength` in
 * [0,1] scales how visible the texture marks are; 0 returns `null` so the
 * caller uses a flat fill. Returns `null` when canvas 2D isn't available.
 */
export function getMaterialTile(material: Material, fill: string, strength: number): MaterialTile | null {
  const s = Math.round(Math.max(0, Math.min(1, strength)) * 4) / 4; // bucket to quarters
  if (s === 0) return null;
  if (material.id === "matte" || material.id === "plastic" || material.id === "glass") return null;
  const key = `${material.id}|${fill}|${s}`;
  const hit = tileCache.get(key);
  if (hit !== undefined) return hit;
  const tile = paintTile(material.id, fill, s);
  if (tileCache.size >= TILE_CACHE_CAP) {
    const first = tileCache.keys().next().value;
    if (first !== undefined) tileCache.delete(first);
  }
  tileCache.set(key, tile);
  return tile;
}

/** Test hook. */
export function clearMaterialTileCache() {
  tileCache.clear();
}

/** Deterministic PRNG so every tile is identical across sessions. */
function makeRng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return ((s >>> 0) % 100000) / 100000;
  };
}

function seedFor(id: string, fill: string) {
  let h = 2166136261;
  for (const ch of id + fill) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function paintTile(id: MaterialId, fill: string, strength: number): MaterialTile | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = TILE_PX;
  canvas.height = TILE_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const rng = makeRng(seedFor(id, fill));
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  ctx.globalAlpha = strength;
  const light = shade(fill, 0.22);
  const dark = shade(fill, -0.28);
  const lightSoft = shade(fill, 0.1);
  const darkSoft = shade(fill, -0.12);

  switch (id) {
    case "fabric":
      paintWeave(ctx, lightSoft, darkSoft, 3, rng);
      break;
    case "boucle":
      paintBoucle(ctx, light, darkSoft, rng);
      break;
    case "velvet":
      paintVelvet(ctx, fill, light, dark);
      break;
    case "leather":
      paintLeather(ctx, light, dark, rng);
      break;
    case "wood-light":
    case "wood-mid":
    case "wood-dark":
      paintWood(ctx, light, dark, rng, id === "wood-dark" ? 0.5 : 0.36);
      break;
    case "marble":
      paintMarble(ctx, fill, rng);
      break;
    case "quartz":
      paintQuartz(ctx, fill, rng);
      break;
    case "granite":
      paintGranite(ctx, fill, rng);
      break;
    case "quartzite":
      paintQuartzite(ctx, fill, rng);
      break;
    case "soapstone":
      paintSoapstone(ctx, fill, rng);
      break;
    case "butcher-block":
      paintButcherBlock(ctx, fill);
      break;
    case "concrete":
      paintConcrete(ctx, fill, rng);
      break;
    case "terrazzo":
      paintTerrazzo(ctx, fill, rng);
      break;
    case "laminate":
      paintLaminate(ctx, fill, rng);
      break;
    case "tile":
      paintTileGrid(ctx, fill);
      break;
    case "metal":
      paintBrushed(ctx, light, dark, rng);
      break;
    case "rattan":
      paintRattan(ctx, lightSoft, dark);
      break;
    case "wool-rug":
      paintWool(ctx, lightSoft, darkSoft, rng);
      break;
    case "ceramic":
      paintCeramic(ctx, light);
      break;
    case "foliage":
      paintFoliage(ctx, fill, rng);
      break;
    default:
      break;
  }
  ctx.globalAlpha = 1;
  let url: string | null = null;
  return {
    canvas,
    dataUrl: () => {
      if (!url) url = canvas.toDataURL("image/png");
      return url;
    },
  };
}

// --- painters -------------------------------------------------------------

function paintWeave(ctx: CanvasRenderingContext2D, light: string, dark: string, cell: number, rng: () => number) {
  const base = ctx.globalAlpha;
  for (let y = 0; y < TILE_PX; y += cell) {
    for (let x = 0; x < TILE_PX; x += cell) {
      const odd = ((x / cell + y / cell) | 0) % 2 === 0;
      ctx.fillStyle = odd ? light : dark;
      ctx.globalAlpha = base * (0.3 + rng() * 0.2);
      ctx.fillRect(x, y, cell - 1, cell - 1);
    }
  }
  ctx.globalAlpha = base;
}

function paintBoucle(ctx: CanvasRenderingContext2D, light: string, dark: string, rng: () => number) {
  const base = ctx.globalAlpha;
  for (let i = 0; i < 520; i++) {
    const x = rng() * TILE_PX;
    const y = rng() * TILE_PX;
    const r = 1 + rng() * 2.2;
    ctx.fillStyle = rng() > 0.45 ? light : dark;
    ctx.globalAlpha = base * (0.25 + rng() * 0.35);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = base;
}

function paintVelvet(ctx: CanvasRenderingContext2D, fill: string, light: string, dark: string) {
  const base = ctx.globalAlpha;
  const g = ctx.createLinearGradient(0, 0, TILE_PX, 0);
  g.addColorStop(0, dark);
  g.addColorStop(0.35, fill);
  g.addColorStop(0.6, light);
  g.addColorStop(1, dark);
  ctx.globalAlpha = base * 0.28;
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  // Very faint nap: a soft second sheen band offset from the first reads as
  // crushed pile without turning into stripes.
  const g2 = ctx.createLinearGradient(0, 0, TILE_PX * 0.6, TILE_PX);
  g2.addColorStop(0, "rgba(255,255,255,0)");
  g2.addColorStop(0.5, light);
  g2.addColorStop(1, "rgba(255,255,255,0)");
  ctx.globalAlpha = base * 0.1;
  ctx.fillStyle = g2;
  ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  ctx.globalAlpha = base;
}

function paintLeather(ctx: CanvasRenderingContext2D, light: string, dark: string, rng: () => number) {
  const base = ctx.globalAlpha;
  // Fine grain speckle — kept faint so it reads as texture, not dirt.
  for (let i = 0; i < 700; i++) {
    ctx.fillStyle = rng() > 0.5 ? light : dark;
    ctx.globalAlpha = base * (0.04 + rng() * 0.09);
    ctx.fillRect(rng() * TILE_PX, rng() * TILE_PX, 1.25, 1.25);
  }
  // Soft sheen.
  const g = ctx.createRadialGradient(TILE_PX * 0.3, TILE_PX * 0.3, 4, TILE_PX * 0.3, TILE_PX * 0.3, TILE_PX * 0.9);
  g.addColorStop(0, light);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.globalAlpha = base * 0.18;
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  // A few creases.
  ctx.globalAlpha = base * 0.22;
  ctx.strokeStyle = dark;
  ctx.lineWidth = 0.8;
  for (let i = 0; i < 6; i++) {
    const x = rng() * TILE_PX;
    const y = rng() * TILE_PX;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.bezierCurveTo(x + 10, y + rng() * 8 - 4, x + 20, y + rng() * 8 - 4, x + 30, y + rng() * 6 - 3);
    ctx.stroke();
  }
  ctx.globalAlpha = base;
}

function paintWood(ctx: CanvasRenderingContext2D, light: string, dark: string, rng: () => number, contrast: number) {
  const base = ctx.globalAlpha;
  const lines = 14;
  for (let i = 0; i < lines; i++) {
    const y0 = (i / lines) * TILE_PX + rng() * 4;
    const amp = 1 + rng() * 3;
    const phase = rng() * Math.PI * 2;
    const isDark = rng() > 0.4;
    ctx.strokeStyle = isDark ? dark : light;
    ctx.lineWidth = 0.6 + rng() * 1.4;
    ctx.globalAlpha = base * contrast * (0.35 + rng() * 0.5);
    ctx.beginPath();
    for (let x = 0; x <= TILE_PX; x += 4) {
      // Period equals the tile width so the grain wraps seamlessly.
      const y = y0 + Math.sin((x / TILE_PX) * Math.PI * 2 + phase) * amp;
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  // Subtle plank-wide tonal variation.
  const g = ctx.createLinearGradient(0, 0, 0, TILE_PX);
  g.addColorStop(0, light);
  g.addColorStop(0.5, "rgba(0,0,0,0)");
  g.addColorStop(1, dark);
  ctx.globalAlpha = base * 0.08;
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  ctx.globalAlpha = base;
}

function paintMarble(ctx: CanvasRenderingContext2D, fill: string, rng: () => number) {
  const base = ctx.globalAlpha;
  const { r, g, b } = hexToRgb(fill);
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  const vein = lum > 0.5 ? "#8d8f97" : "#d7d7dc";
  ctx.strokeStyle = vein;
  for (let i = 0; i < 5; i++) {
    ctx.lineWidth = 0.6 + rng() * 1.2;
    ctx.globalAlpha = base * (0.12 + rng() * 0.22);
    const y = rng() * TILE_PX;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(TILE_PX * 0.3, y + rng() * 40 - 20, TILE_PX * 0.7, y + rng() * 40 - 20, TILE_PX, y + rng() * 10 - 5);
    ctx.stroke();
  }
  // Cloudy tone.
  const cloud = ctx.createRadialGradient(TILE_PX * 0.6, TILE_PX * 0.4, 6, TILE_PX * 0.6, TILE_PX * 0.4, TILE_PX * 0.8);
  cloud.addColorStop(0, rgbToHex({ r: r * 0.92, g: g * 0.92, b: b * 0.92 }));
  cloud.addColorStop(1, "rgba(0,0,0,0)");
  ctx.globalAlpha = base * 0.25;
  ctx.fillStyle = cloud;
  ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  ctx.globalAlpha = base;
}

function paintBrushed(ctx: CanvasRenderingContext2D, light: string, dark: string, rng: () => number) {
  const base = ctx.globalAlpha;
  for (let y = 0; y < TILE_PX; y += 1) {
    ctx.strokeStyle = rng() > 0.5 ? light : dark;
    ctx.globalAlpha = base * (0.05 + rng() * 0.16);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(TILE_PX, y + 0.5);
    ctx.stroke();
  }
  const g = ctx.createLinearGradient(0, 0, TILE_PX, TILE_PX);
  g.addColorStop(0, light);
  g.addColorStop(0.5, "rgba(0,0,0,0)");
  g.addColorStop(1, dark);
  ctx.globalAlpha = base * 0.16;
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  ctx.globalAlpha = base;
}

function paintRattan(ctx: CanvasRenderingContext2D, light: string, dark: string) {
  const base = ctx.globalAlpha;
  ctx.lineWidth = 1.2;
  const step = 8;
  ctx.globalAlpha = base * 0.4;
  for (let i = -TILE_PX; i < TILE_PX * 2; i += step) {
    ctx.strokeStyle = dark;
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + TILE_PX, TILE_PX);
    ctx.stroke();
    ctx.strokeStyle = light;
    ctx.beginPath();
    ctx.moveTo(i + TILE_PX, 0);
    ctx.lineTo(i, TILE_PX);
    ctx.stroke();
  }
  ctx.globalAlpha = base;
}

function paintWool(ctx: CanvasRenderingContext2D, light: string, dark: string, rng: () => number) {
  const base = ctx.globalAlpha;
  for (let i = 0; i < 2200; i++) {
    ctx.fillStyle = rng() > 0.5 ? light : dark;
    ctx.globalAlpha = base * (0.1 + rng() * 0.25);
    ctx.fillRect(rng() * TILE_PX, rng() * TILE_PX, 1.2, 1.2);
  }
  ctx.globalAlpha = base;
}

function paintCeramic(ctx: CanvasRenderingContext2D, light: string) {
  const base = ctx.globalAlpha;
  const g = ctx.createRadialGradient(TILE_PX * 0.35, TILE_PX * 0.35, 2, TILE_PX * 0.35, TILE_PX * 0.35, TILE_PX);
  g.addColorStop(0, light);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.globalAlpha = base * 0.35;
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  ctx.globalAlpha = base;
}

function paintQuartz(ctx: CanvasRenderingContext2D, fill: string, rng: () => number) {
  const base = ctx.globalAlpha;
  const light = shade(fill, 0.16);
  const dark = shade(fill, -0.18);
  // Fine, even speckle — engineered stone, not a coarse mineral.
  for (let i = 0; i < 700; i++) {
    ctx.fillStyle = rng() > 0.55 ? light : dark;
    ctx.globalAlpha = base * (0.12 + rng() * 0.22);
    ctx.beginPath();
    ctx.arc(rng() * TILE_PX, rng() * TILE_PX, 0.7 + rng() * 1.6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = base;
}

function paintGranite(ctx: CanvasRenderingContext2D, fill: string, rng: () => number) {
  const base = ctx.globalAlpha;
  const colors = [shade(fill, 0.22), shade(fill, -0.32), mix(fill, "#8a5a3a", 0.5), mix(fill, "#1c1c22", 0.35)];
  for (let i = 0; i < 280; i++) {
    const x = rng() * TILE_PX;
    const y = rng() * TILE_PX;
    const r = 1.4 + rng() * 3.2;
    ctx.fillStyle = colors[(rng() * colors.length) | 0];
    ctx.globalAlpha = base * (0.28 + rng() * 0.4);
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * (0.55 + rng() * 0.5), rng() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
    // A few grains cluster, the way feldspar does.
    if (rng() > 0.72) {
      for (let k = 0; k < 3; k++) {
        ctx.globalAlpha = base * (0.2 + rng() * 0.3);
        ctx.beginPath();
        ctx.arc(x + rng() * 8 - 4, y + rng() * 8 - 4, 0.8 + rng() * 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  ctx.globalAlpha = base;
}

function paintQuartzite(ctx: CanvasRenderingContext2D, fill: string, rng: () => number) {
  const base = ctx.globalAlpha;
  const { r, g, b } = hexToRgb(fill);
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  const vein = lum > 0.55 ? shade(fill, -0.48) : shade(fill, 0.5);
  const flash = lum > 0.5 ? "#f7f7f5" : shade(fill, 0.28);
  for (let i = 0; i < 6; i++) {
    ctx.strokeStyle = vein;
    ctx.lineWidth = 0.8 + rng() * 2.2;
    ctx.globalAlpha = base * (0.22 + rng() * 0.3);
    ctx.beginPath();
    let x = 0;
    let y = rng() * TILE_PX;
    ctx.moveTo(x, y);
    const sparks: { x: number; y: number }[] = [];
    while (x < TILE_PX) {
      x += 14 + rng() * 24;
      y += rng() * 36 - 18;
      const px = Math.min(TILE_PX, x);
      ctx.lineTo(px, y);
      if (rng() > 0.45) sparks.push({ x: px, y });
    }
    ctx.stroke();
    ctx.fillStyle = flash;
    for (const s of sparks) {
      ctx.globalAlpha = base * (0.35 + rng() * 0.4);
      ctx.beginPath();
      ctx.arc(s.x, s.y, 0.8 + rng() * 1.3, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = base;
}

function paintSoapstone(ctx: CanvasRenderingContext2D, fill: string, rng: () => number) {
  const base = ctx.globalAlpha;
  for (let i = 0; i < 6; i++) {
    const x = rng() * TILE_PX;
    const y = rng() * TILE_PX;
    const rad = 28 + rng() * 40;
    const g = ctx.createRadialGradient(x, y, 4, x, y, rad);
    g.addColorStop(0, rng() > 0.5 ? shade(fill, 0.14) : shade(fill, -0.16));
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.globalAlpha = base * 0.55;
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  }
  ctx.strokeStyle = shade(fill, 0.1);
  ctx.lineWidth = 1.2;
  for (let i = 0; i < 3; i++) {
    const y = rng() * TILE_PX;
    ctx.globalAlpha = base * 0.12;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(TILE_PX * 0.35, y + 8, TILE_PX * 0.7, y - 8, TILE_PX, y + 2);
    ctx.stroke();
  }
  ctx.globalAlpha = base;
}

function paintButcherBlock(ctx: CanvasRenderingContext2D, fill: string) {
  const base = ctx.globalAlpha;
  // 2" boards running the long axis. Four of them fill the 8" tile so the
  // repeat is seamless; `directional` rotates this onto the part's length.
  const strip = TILE_PX / 4;
  const tones = [0.1, -0.08, 0.02, -0.14];
  for (let i = 0; i < 4; i++) {
    ctx.globalAlpha = base * 0.72;
    ctx.fillStyle = shade(fill, tones[i]);
    ctx.fillRect(0, i * strip, TILE_PX, strip);
    ctx.strokeStyle = shade(fill, -0.28);
    ctx.lineWidth = 0.8;
    ctx.globalAlpha = base * 0.28;
    const y = i * strip + strip * 0.38;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(TILE_PX * 0.35, y + 2, TILE_PX * 0.7, y - 2, TILE_PX, y);
    ctx.stroke();
  }
  ctx.globalAlpha = base * 0.45;
  ctx.strokeStyle = shade(fill, -0.4);
  ctx.lineWidth = 1.1;
  for (let i = 1; i < 4; i++) {
    ctx.beginPath();
    ctx.moveTo(0, i * strip);
    ctx.lineTo(TILE_PX, i * strip);
    ctx.stroke();
  }
  ctx.globalAlpha = base;
}

function paintConcrete(ctx: CanvasRenderingContext2D, fill: string, rng: () => number) {
  const base = ctx.globalAlpha;
  for (let i = 0; i < 7; i++) {
    const x = rng() * TILE_PX;
    const y = rng() * TILE_PX;
    const rad = 22 + rng() * 36;
    const g = ctx.createRadialGradient(x, y, 2, x, y, rad);
    g.addColorStop(0, rng() > 0.5 ? shade(fill, 0.1) : shade(fill, -0.12));
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.globalAlpha = base * 0.5;
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, TILE_PX, TILE_PX);
  }
  ctx.fillStyle = shade(fill, -0.4);
  for (let i = 0; i < 70; i++) {
    ctx.globalAlpha = base * (0.18 + rng() * 0.28);
    ctx.beginPath();
    ctx.arc(rng() * TILE_PX, rng() * TILE_PX, 0.6 + rng() * 1.1, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = base;
}

function paintTerrazzo(ctx: CanvasRenderingContext2D, fill: string, rng: () => number) {
  const base = ctx.globalAlpha;
  const chips = [
    shade(fill, 0.3),
    shade(fill, -0.38),
    mix(fill, "#c45c4a", 0.62),
    mix(fill, "#3d6b8a", 0.55),
    mix(fill, "#d8c27a", 0.5),
  ];
  for (let i = 0; i < 42; i++) {
    ctx.fillStyle = chips[i % chips.length];
    ctx.globalAlpha = base * (0.62 + rng() * 0.35);
    ctx.beginPath();
    ctx.ellipse(
      rng() * TILE_PX,
      rng() * TILE_PX,
      3 + rng() * 9,
      2 + rng() * 6,
      rng() * Math.PI,
      0,
      Math.PI * 2
    );
    ctx.fill();
  }
  ctx.globalAlpha = base;
}

function paintLaminate(ctx: CanvasRenderingContext2D, fill: string, rng: () => number) {
  const base = ctx.globalAlpha;
  // Printed stone: soft, low-contrast clouds on a regular beat so it reads
  // flatter than quarried quartz.
  const step = 32;
  for (let y = 0; y < TILE_PX; y += step) {
    for (let x = 0; x < TILE_PX; x += step) {
      const jx = x + 6 + rng() * 18;
      const jy = y + 6 + rng() * 18;
      const g = ctx.createRadialGradient(jx, jy, 1, jx, jy, 16);
      g.addColorStop(0, shade(fill, rng() > 0.5 ? 0.07 : -0.08));
      g.addColorStop(1, "rgba(0,0,0,0)");
      ctx.globalAlpha = base * 0.4;
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, TILE_PX, TILE_PX);
    }
  }
  ctx.globalAlpha = base;
}

function paintTileGrid(ctx: CanvasRenderingContext2D, fill: string) {
  const base = ctx.globalAlpha;
  const grout = shade(fill, -0.42);
  const tile = TILE_PX / 2;
  const groutPx = 3;
  const tones = [0.06, -0.04, 0.01, -0.08];
  ctx.globalAlpha = base * 0.8;
  for (let row = 0; row < 2; row++) {
    for (let col = 0; col < 2; col++) {
      ctx.fillStyle = shade(fill, tones[row * 2 + col]);
      ctx.fillRect(
        col * tile + groutPx / 2,
        row * tile + groutPx / 2,
        tile - groutPx,
        tile - groutPx
      );
    }
  }
  ctx.globalAlpha = base * 0.9;
  ctx.fillStyle = grout;
  ctx.fillRect(0, tile - groutPx / 2, TILE_PX, groutPx);
  ctx.fillRect(tile - groutPx / 2, 0, groutPx, TILE_PX);
  ctx.fillRect(0, 0, TILE_PX, groutPx / 2);
  ctx.fillRect(0, TILE_PX - groutPx / 2, TILE_PX, groutPx / 2);
  ctx.fillRect(0, 0, groutPx / 2, TILE_PX);
  ctx.fillRect(TILE_PX - groutPx / 2, 0, groutPx / 2, TILE_PX);
  ctx.globalAlpha = base;
}

function paintFoliage(ctx: CanvasRenderingContext2D, fill: string, rng: () => number) {
  const base = ctx.globalAlpha;
  const light = shade(fill, 0.2);
  const dark = shade(fill, -0.3);
  for (let i = 0; i < 90; i++) {
    const x = rng() * TILE_PX;
    const y = rng() * TILE_PX;
    const r = 3 + rng() * 6;
    ctx.fillStyle = rng() > 0.5 ? light : dark;
    ctx.globalAlpha = base * (0.2 + rng() * 0.3);
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * 0.6, rng() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = base;
}
